/**
 * Keeps floating action buttons clear of the sticky add-to-cart bar, and out of
 * the way while a chat app (Shopify Inbox and similar) has its window open.
 */

const STACK_GAP = 12;

class FloatingActionsAnchor extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {HTMLElement | null} */
  #stickyBar = null;

  /** @type {number | undefined} */
  #chatPollTimer;

  connectedCallback() {
    this.#stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    this.#syncStackOffset();

    if (this.#stickyBar) {
      this.#stickyResizeObserver = new ResizeObserver(() => this.#syncStackOffset());
      this.#stickyResizeObserver.observe(this.#stickyBar);
    }

    // Chat apps render markup the theme does not control, so the open window is
    // detected by hit-testing rather than by selector.
    this.#chatPollTimer = setInterval(this.#syncChatPanelState, 500);
    this.#syncChatPanelState();

    document.addEventListener('sticky-atc:visibility', this.#handleStickyVisibility);
    window.addEventListener('resize', this.#syncStackOffset, { passive: true });
  }

  disconnectedCallback() {
    this.#stickyResizeObserver?.disconnect();
    document.removeEventListener('sticky-atc:visibility', this.#handleStickyVisibility);
    window.removeEventListener('resize', this.#syncStackOffset);
    clearInterval(this.#chatPollTimer);
    document.documentElement.removeAttribute('data-chat-widget-open');
  }

  /** @param {CustomEvent<{ visible: boolean }>} event */
  #handleStickyVisibility = (event) => {
    if (event.detail?.visible) {
      this.#syncStackOffset();
    } else {
      document.documentElement.style.removeProperty('--sticky-atc-stack-offset');
    }
  };

  #syncStackOffset = () => {
    const root = document.documentElement;
    const stickyBar = this.#stickyBar ?? document.querySelector('.sticky-add-to-cart__bar');

    if (!stickyBar || stickyBar.dataset.stuck !== 'true') {
      root.style.removeProperty('--sticky-atc-stack-offset');
      return;
    }

    root.style.setProperty('--sticky-atc-stack-offset', `${stickyBar.offsetHeight + STACK_GAP}px`);
  };

  /**
   * Hides this button while a floating panel covers the middle of the viewport,
   * which is where an opened chat window sits.
   */
  #syncChatPanelState = () => {
    const { innerWidth: vw, innerHeight: vh } = window;
    const anchoredLeft = this.getBoundingClientRect().left < vw / 2;
    const x = anchoredLeft ? 48 : vw - 48;

    document.documentElement.toggleAttribute('data-chat-widget-open', this.#hasFloatingElementAt(x, vh * 0.5));
  };

  /**
   * @param {number} x
   * @param {number} y
   * @returns {boolean} Whether a foreign floating element is painted at this point.
   */
  #hasFloatingElementAt(x, y) {
    if (x < 0 || y < 0) return false;

    for (const element of document.elementsFromPoint(x, y)) {
      if (element === document.body || element === document.documentElement) return false;
      if (element === this || this.contains(element)) continue;

      return hasFixedAncestor(element);
    }

    return false;
  }
}

/**
 * @param {Element} element
 * @returns {boolean} Whether the element is rendered inside a fixed subtree.
 */
function hasFixedAncestor(element) {
  /** @type {Element | null} */
  let node = element;

  while (node && node !== document.body) {
    if (getComputedStyle(node).position === 'fixed') return true;
    node = node.parentElement;
  }

  return false;
}

if (!customElements.get('floating-actions-anchor')) {
  customElements.define('floating-actions-anchor', FloatingActionsAnchor);
}
